import { Badge, Button, Modal } from '@payaca/ambrosia';
import { useRegion } from '@/api/rest/users/useRegion';
import moment from 'moment-timezone';
import { FC, PropsWithChildren, useMemo, useState } from 'react';

import EntityCard from '@payaca/components/entityCard/EntityCard';
import FeedbackMessage from '@payaca/components/feedbackMessage/FeedbackMessage';
import ResponsiveViewWrapper from '@payaca/components/responsiveViewWrapper/ResponsiveViewWrapper';
import * as jobPaymentsActions from '@payaca/store/jobPayments/jobPaymentsActions';
import ConfirmPaymentModal from '../confirmPaymentModal/ConfirmPaymentModal';

import { currencyPrice } from '@payaca/helpers/financeHelper';
import {
  DateFormats,
  getInternationalMomentDateFormatByRegion,
} from '@payaca/helpers/internationalHelper';
import { getPaymentMethodLabel } from '@payaca/helpers/jobPaymentHelper';

import { usePermissions } from '@/hooks/usePermissions';

import { usePayment } from '@payaca/store/hooks/appState';

import { PaymentsPermissions } from '@payaca/permissions/payments/payments.permissions';

import { PaymentMethodType } from '@payaca/types/jobPaymentTypes';

import { PrelineColourVariant } from '@payaca/types/preline';
import { useDispatch } from 'react-redux';
import './PaymentCard.sass';

interface Props {
  paymentId: number;
  onConfirmPaymentSuccess?: () => void;
}

const PaymentCard: FC<PropsWithChildren<Props>> = ({
  paymentId,
  onConfirmPaymentSuccess,
}) => {
  const dispatch = useDispatch();
  const [showConfirmPaymentModal, setShowConfirmPaymentModal] = useState(false);
  const [showConfirmFailedPaymentModal, setShowConfirmFailedPaymentModal] =
    useState(false);
  const payment = usePayment(paymentId);
  const { userHasRequiredPermission } = usePermissions();
  const region = useRegion();
  const [isConfirmingFailed, setIsConfirmingFailed] = useState(false);

  const { paymentDate, paymentDateLabel, paymentStatus, badgeColour } =
    useMemo<{
      paymentStatus?: string;
      badgeColour?: PrelineColourVariant;
      paymentDate?: Date;
      paymentDateLabel?: string;
    }>(() => {
      if (!payment) {
        return {
          paymentStatus: undefined,
          badgeColour: undefined,
          paymentDate: undefined,
          paymentDateLabel: undefined,
        };
      }
      if (payment.paymentFailedConfirmationAt) {
        return {
          paymentStatus: 'Failed',
          badgeColour: 'red',
          paymentDate: payment.paymentFailedConfirmationAt,
          paymentDateLabel: 'Failed',
        };
      }
      if (payment.paymentCompletedConfirmationAt) {
        return {
          paymentStatus: 'Complete',
          badgeColour: 'green',
          paymentDate: payment.paymentCompletedConfirmationAt,
          paymentDateLabel: 'Confirmed',
        };
      }
      return {
        paymentStatus: 'Pending',
        badgeColour: 'yellow',
        paymentDate:
          payment.bacsPaymentMadeAt || payment.stripePaymentAttemptedAt,
        paymentDateLabel: 'Attempted',
      };
    }, [payment]);

  const timestampDateRegionalFormat = useMemo(
    () =>
      getInternationalMomentDateFormatByRegion(
        DateFormats.TIMESTAMP_SHORT,
        region
      ),
    [region]
  );

  const confirmPaymentFailed = () => {
    setIsConfirmingFailed(true);
    dispatch(
      jobPaymentsActions.requestConfirmJobPayment(
        {
          jobPaymentId: paymentId,
          isPaymentReceived: false,
        },
        () => {
          setShowConfirmFailedPaymentModal(false);
          setIsConfirmingFailed(false);
          onConfirmPaymentSuccess?.();
        }
      )
    );
  };

  const quickActions = useMemo(() => {
    const quickActions = [];

    if (
      payment?.bacsPaymentMadeAt &&
      !payment.paymentFailedConfirmationAt &&
      !payment.paymentCompletedConfirmationAt &&
      userHasRequiredPermission([PaymentsPermissions.CONFIRM_PAYMENT])
    ) {
      quickActions.push({
        actionName: 'Confirm payment',
        actionBehaviour: () => setShowConfirmPaymentModal(true),
      });
    }

    if (
      payment?.paymentMethod != PaymentMethodType.STRIPE &&
      payment?.paymentMethod != PaymentMethodType.GRANT &&
      payment?.paymentCompletedConfirmationAt &&
      userHasRequiredPermission([PaymentsPermissions.CONFIRM_PAYMENT])
    ) {
      quickActions.push({
        actionName: 'Payment not received',
        actionBehaviour: () => setShowConfirmFailedPaymentModal(true),
      });
    }

    return quickActions;
  }, [payment, userHasRequiredPermission]);

  if (!payment) return null;

  return (
    <>
      <EntityCard
        className="payment-card"
        quickActionsConfig={{
          recordId: paymentId,
          renderDisabledIfNoActions: true,
          quickActions: quickActions,
        }}
      >
        <ResponsiveViewWrapper
          className="payment-card-content"
          downBreakpointSm={500}
        >
          <>
            <dl>
              <dt>Amount</dt>
              <dd>{currencyPrice(payment.paymentValue, region)}</dd>
            </dl>
            <div className="min-w-24 justify-self-center">
              <Badge variant="soft" colour={badgeColour}>
                {paymentStatus || ''}
              </Badge>
            </div>
            <dl>
              <dt>Method</dt>
              <dd>{getPaymentMethodLabel(payment.paymentMethod)}</dd>
            </dl>
            <dl>
              <dt>{paymentDateLabel}</dt>
              <dd>{moment(paymentDate).format(timestampDateRegionalFormat)}</dd>
            </dl>
          </>
        </ResponsiveViewWrapper>
      </EntityCard>
      {payment.paymentMethod == PaymentMethodType.FINANCE_PAYOUT &&
        !!payment.paymentCompletedConfirmationAt && (
          <div className="additional-information">
            <FeedbackMessage
              iconBefore={'info-circle.3'}
              message={'Fees may have been deducted from this payment'}
            />
          </div>
        )}
      <ConfirmPaymentModal
        jobPaymentId={paymentId}
        isOpen={showConfirmPaymentModal}
        onClose={() => {
          setShowConfirmPaymentModal(false);
        }}
        confirmPaymentCallback={() => {
          setShowConfirmPaymentModal(false);
          onConfirmPaymentSuccess?.();
        }}
      />
      <Modal
        isOpen={showConfirmFailedPaymentModal}
        onClose={() => setShowConfirmFailedPaymentModal(false)}
        title={'Are you sure?'}
      >
        <Modal.Body>
          <p>You previously marked this payment as having been received. </p>
          <p>If you wish to undo this, please continue.</p>
        </Modal.Body>
        <Modal.Footer>
          <Modal.Footer.Actions>
            <Button
              onClick={() => confirmPaymentFailed()}
              isProcessing={isConfirmingFailed}
              replaceContentWhenProcessing
            >
              Mark payment as failed
            </Button>
          </Modal.Footer.Actions>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default PaymentCard;
